import React, { useState, useEffect, useCallback } from "react";
import PriceBar        from "./components/PriceBar";
import DominanceBar    from "./components/DominanceBar";
import CoinTabs        from "./components/CoinTabs";
import BigLiqBanner    from "./components/BigLiqBanner";
import SignalPanel     from "./components/SignalPanel";
import TwapPanel       from "./components/TwapPanel";
import LongShortPanel  from "./components/LongShortPanel";
import TopTradersPanel from "./components/TopTradersPanel";
import OIPanel         from "./components/OIPanel";
import HeatmapEmbed    from "./components/HeatmapEmbed";
import FundingPanel    from "./components/FundingPanel";
import TechPanel       from "./components/TechPanel";
import SentimentPanel  from "./components/SentimentPanel";
import AlertsPanel     from "./components/AlertsPanel";
import NewsPanel       from "./components/NewsPanel";
import EventsPanel     from "./components/EventsPanel";
import AlertLog        from "./components/AlertLog";
import "./App.css";


const api = (path, opts={}) => fetch(path, opts).then(r=>r.json());

export default function App() {
  const [coins,      setCoins]      = useState({});
  const [active,     setActive]     = useState("HYPE");
  const [coinData,   setCoinData]   = useState({});
  const [globalData, setGlobalData] = useState({});
  const [alertLog,   setAlertLog]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [tgStatus,   setTgStatus]   = useState(null);

  useEffect(() => {
    api("/api/coins").then(d => {
      setCoins(d.coins||{}); setActive(d.default||"HYPE");
    }).catch(console.error);
  }, []);

  const fetchAll = useCallback(async () => {
    if (!Object.keys(coins).length) return;
    try {
      const [g, log, ...coinRes] = await Promise.all([
        api("/api/global"),
        api("/api/alert-log"),
        ...Object.keys(coins).map(s => api(`/api/coin/${s}`).then(d=>[s,d])),
      ]);
      setGlobalData(g); setAlertLog(log.data||[]);
      const nd={};
      coinRes.forEach(([s,d])=>{ nd[s]=d; });
      setCoinData(nd);
      setLastUpdate(new Date().toLocaleTimeString("ko-KR"));
    } catch(e){ console.error(e); }
    finally { setLoading(false); }
  }, [coins]);

  useEffect(() => {
    if (!Object.keys(coins).length) return;
    fetchAll();
    const id = setInterval(fetchAll, 60000);
    return () => clearInterval(id);
  }, [fetchAll, coins]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetch("/api/refresh", {method:"POST"});
    await fetchAll(); setRefreshing(false);
  };

  const handleTestTg = async () => {
    setTgStatus("sending");
    const r = await fetch("/api/test-telegram",{method:"POST"}).then(r=>r.json()).catch(()=>({status:"failed"}));
    setTgStatus(r.status==="ok"?"ok":"failed");
    setTimeout(()=>setTgStatus(null),4000);
  };

  const cd=coinData[active]||{};
  const level=cd.twap?.alert_level||"normal";
  const isHype=active==="HYPE";
  const dotClass="status-dot"+(level==="urgent"?" urgent":level==="warn"?" warn":"");

  if (loading) return (
    <div className="loading-screen"><div className="spinner"/><p>데이터 불러오는 중...</p></div>
  );

  return (
    <div>
      <header className="header">
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <span className="logo">📊 크립토 대시보드</span>
          <span className={dotClass}/>
          <span style={{fontSize:11,color:"var(--muted)"}}>1분 갱신</span>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          {lastUpdate&&<span style={{fontSize:11,color:"var(--muted)"}}>{lastUpdate}</span>}
          <button className="btn btn-gray" onClick={handleTestTg}>
            {tgStatus==="sending"?"전송 중...":tgStatus==="ok"?"✅ 발송됨":tgStatus==="failed"?"❌ 실패":"📨 텔레그램 테스트"}
          </button>
          <button className="btn btn-blue" onClick={handleRefresh} disabled={refreshing}>
            {refreshing?"⏳":"🔄"} 새로고침
          </button>
        </div>
      </header>

      <PriceBar prices={globalData.prices} coins={coins}/>
      <DominanceBar data={globalData.dominance}/>
      <CoinTabs coins={coins} active={active} onChange={setActive} prices={globalData.prices}/>
      {cd.liquidations?.big_events?.length>0&&<BigLiqBanner events={cd.liquidations.big_events} coin={active}/>}

      <main className="grid">
        <section className="card card--wide" style={{border:`1.5px solid ${cd.signal?.direction==="long"?"rgba(34,197,94,.4)":cd.signal?.direction==="short"?"rgba(239,68,68,.4)":"#1e2433"}`}}>
          <h2 className="card-title">🎯 종합 포지션 신호 · 신뢰도</h2>
          <SignalPanel data={cd.signal}/>
        </section>
        {isHype&&<section className={`card card--wide ${level!=="normal"?level:""}`}><h2 className="card-title">📊 HYPE TWAP</h2><TwapPanel data={cd.twap}/></section>}
        <section className="card"><h2 className="card-title">📈 롱/숏 비율</h2><LongShortPanel data={cd.long_short}/></section>
        <section className={`card ${cd.top_traders?.any_triggered?"warn":""}`}><h2 className="card-title">🐋 Top Traders</h2><TopTradersPanel data={cd.top_traders}/></section>
        <section className="card card--wide"><h2 className="card-title">💰 오픈 인터레스트</h2><OIPanel data={cd.open_interest}/></section>
        <section className="card card--wide"><h2 className="card-title">🔥 청산 히트맵</h2><HeatmapEmbed defaultCoin={active}/></section>
        <section className="card"><h2 className="card-title">💸 펀딩비</h2><FundingPanel data={cd.funding}/></section>
        <section className="card"><h2 className="card-title">📐 RSI · MACD</h2><TechPanel data={cd.technicals}/></section>
        <section className="card"><h2 className="card-title">🧠 시장 심리</h2><SentimentPanel data={globalData.sentiment}/></section>
        <section className="card"><h2 className="card-title">🔔 가격 알림</h2><AlertsPanel coin={active} price={cd.price?.price}/></section>
        <section className="card card--wide"><h2 className="card-title">📰 뉴스</h2><NewsPanel data={globalData.news}/></section>
        <section className="card"><h2 className="card-title">📅 예정 발표</h2><EventsPanel data={globalData.events}/></section>
        <section className="card"><h2 className="card-title">📋 알림 이력</h2><AlertLog logs={alertLog}/></section>
      </main>
    </div>
  );
}
